import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';
import { Subject, takeUntil } from 'rxjs';
import { FileType } from '../../models/enums/file_type.enum';
import { CustomPdfFile } from '../../models/enums/pdf_file.interface';
import { CustomFile } from '../../models/interfaces/custom_file.interface';
import { FileResult } from '../../models/interfaces/file_result.interface';
import { AlertService } from '../../core/services/alert/alert.service';
import { ToastService } from '../../core/services/toast/toast.service';
import { UtilsService } from '../../shared/utils/utils.service';

@Component({
    selector: 'enfaf-dropzone',
    imports: [CommonModule, NgxFileDropModule, MatIconModule, MatButtonModule, MatTooltipModule],
    templateUrl: './enfaf-dropzone.component.html',
    styleUrl: './enfaf-dropzone.component.scss'
})
export class EnfafDropzoneComponent implements OnInit, OnDestroy {
  @Input() filesToAccept: string = '*';
  @Input() acceptMultiple = false;
  @Input() acceptLimit = 3;
  @Input() acceptBigFiles = false;
  @Input() limitFilesSize: number | undefined;
  @Input() cleanFiles: Subject<any> = new Subject();
  @Input() color: string = 'primary';
  @Output() filesDropped = new EventEmitter();
  @Output() localFileDeleted = new EventEmitter();

  totalFilesSize = 0;
  files: Array<CustomFile> = [];

  private unsubscribe$ = new Subject<void>();

  constructor(
    private utils: UtilsService,
    private alert: AlertService,
    private toast: ToastService,
    private sanitizer: DomSanitizer,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (!this.filesToAccept) {
      this.filesToAccept = '*';
    }

    this.cleanFiles.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
      this.files = [];
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.acceptLimit && !changes.acceptLimit.firstChange) {
      this.changeDetectorRef.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  async dropped(files: NgxFileDropEntry[]): Promise<void> {
    try {
      if (!files || !files.length) {
        return;
      }

      if (this.acceptLimit && files.length > this.acceptLimit) {
        const maxErrorText = `Máximo ${this.acceptLimit} archivos`;
        return this.toast.presentErrorToast(maxErrorText);
      }

      let errorMsgs = '';
      let receivedFiles: Array<any> = [];

      if (this.acceptMultiple) {
        for (let i = 0; i < files.length; i++) {
          const currentFile = files[i];
          if (currentFile.fileEntry.isFile) {
            const fileEntry = currentFile.fileEntry as FileSystemFileEntry;
            const file = await this.getFileSync(fileEntry);
            if (file.errorMsg) {
              errorMsgs += file.errorMsg;
            } else {
              receivedFiles.push({
                name: currentFile.relativePath,
                file: file.file,
                fileType: file.fileType,
                previewSrc: file.previewSrc
              });
            }
          }
        }
        this.files.push(...receivedFiles);
      } else {
        const currentFile = files[0];
        if (currentFile.fileEntry.isFile) {
          const fileEntry = currentFile.fileEntry as FileSystemFileEntry;
          const file = await this.getFileSync(fileEntry);
          if (file.errorMsg) {
            errorMsgs += file.errorMsg;
          } else {
            receivedFiles = [
              {
                name: currentFile.relativePath,
                file: file.file,
                fileType: file.fileType,
                previewSrc: file.previewSrc
              }
            ];
          }
        }
        this.files = receivedFiles;
      }

      if (this.utils.hasSomething(errorMsgs)) {
        this.toast.presentErrorToast(errorMsgs, 10000);
      }

      this.filesDropped.emit(this.files as any);
    } catch (error) {
      const errorText = 'Hubo un error adjuntando archivos';
      this.toast.presentErrorToast(errorText);
    }
  }

  getFileSync(fileEntry: FileSystemFileEntry): Promise<{
    errorMsg?: string;
    file?: File;
    previewSrc?: string | SafeUrl | null;
    fileType?: string;
  }> {
    return new Promise((resolve, reject) => {
      let errorMsg = null;
      fileEntry.file(async (file: File) => {
        try {
          const fileName = file.name;
          let fileType = FileType.Document;

          if (file.type.indexOf(FileType.Image) > -1) {
            fileType = FileType.Image;
          } else if (file.type.indexOf(FileType.Video) > -1) {
            fileType = FileType.Video;
          } else if (file.type.indexOf('pdf') > -1) {
            fileType = FileType.Document;
          } else if (file.type.indexOf('zip') > -1 || file.type.indexOf('compressed') > -1 || file.type.indexOf('x-rar') > -1) {
            fileType = FileType.Archive;
          }

          if (!this.acceptBigFiles) {
            this.totalFilesSize += file.size;
            if (this.limitFilesSize !== undefined && this.totalFilesSize > this.limitFilesSize) {
              errorMsg = `- El peso total de los archivos no puede exceder los ${this.limitFilesSize / this.utils.oneMegaByte} MB\n`;
              this.totalFilesSize -= file.size;
              resolve({ errorMsg });
            }

            if (fileType === FileType.Image && file.size > this.utils.maxImageSize) {
              errorMsg = `- La imagen '${fileName}' no puede exceder los ${this.utils.maxImageSize / this.utils.oneMegaByte} MB\n`;
              resolve({ errorMsg });
            } else if (fileType === FileType.Video && file.size > this.utils.maxVideoSize) {
              errorMsg = `- El vídeo '${fileName}' no puede exceder los ${this.utils.maxVideoSize / this.utils.oneMegaByte} MB\n`;
              resolve({ errorMsg });
            } else if (fileType === FileType.Document && file.size > this.utils.maxDocumentSize) {
              errorMsg = `- El documento '${fileName}' no puede exceder los ${this.utils.maxDocumentSize / this.utils.oneMegaByte} MB\n`;
              resolve({ errorMsg });
            }
          }

          let previewSrc: SafeUrl | string | null = null;
          if (fileType === FileType.Image) {
            const imgData = await this.utils.previewImageFile(file);
            const previewUnsafeStyle = `background-image: url(${imgData})`;
            const previewSafeStyle = this.sanitizer.bypassSecurityTrustStyle(previewUnsafeStyle);
            previewSrc = previewSafeStyle;
          } else if (fileType === FileType.Video) {
            previewSrc = this.utils.previewVideoFile(file);
          } else if (fileType === FileType.Document && file.type.indexOf('pdf') > -1) {
            previewSrc = (await this.utils.previewImageFile(file)).replace('data:application/pdf;base64,', '');
          }
          resolve({ file, previewSrc, fileType });
        } catch (error) {
          reject(error);
        }
      });
    });
  }

  playVideo(url: SafeUrl): void {
    this.utils.playVideo(url);
  }

  async confirmRemoveFile(i: number): Promise<any> {
    const header = 'Borrar archivo';
    const message = '¿Estás seguro(a) de borrar este archivo? Esta acción no se podrá deshacer.';
    const doNotDeleteFile = 'No borrar';
    const deleteFile = 'Borrar';
    const deleteCategory = await this.alert.presentConfirmActionAlert(header, message, doNotDeleteFile, deleteFile);
    return deleteCategory ? this.removeLocalFile(i) : null;
  }

  removeLocalFile(i: number): void {
    const result: FileResult = {
      index: i,
      fileName: this.files[i].name || ''
    };
    this.files.splice(i, 1);
    this.localFileDeleted.emit(result as any);
  }

  previewDocument(url: any): void {
    this.utils.previewDocument(url, CustomPdfFile.Base64);
  }
}
